// MARK://dhk8/pos/pages/settings/PrintingSettingsPage
// /settings/printing — Configuración del NintaiGO Bridge por terminal.
//
// 6 secciones:
//   1. Estado del bridge — host: ping /v1/version cada 10s (online/outdated/
//      offline). relay: heartbeat de la estación nube (en línea/fuera de línea).
//   2. Conexión del Bridge — HOST (USB local, loopback, botón "Probar conexión")
//      vs VÍA NUBE (Print Relay): teclear el código de estación (texto) →
//      "Vincular" (resolve) → panel con estación + online/offline + jobs
//      recientes + reimprimir + "Probar impresión" (encola y sigue el job).
//   3. Pruebas — test page, abrir cajón, listar impresoras + elegir default
//      (SOLO host: estas hablan al loopback directo).
//   4. Toggles auto-print por superficie (Barra / Comanda DINE_IN / KDS).
//   5. Matriz Método de pago × Apertura de cajón (default: solo CASH).
//   6. Historial breve de impresiones (últimas 5) — leer desde el store.
//
// Aislamiento multi-sucursal (docs/print-relay-design.md): el código de estación
// resuelve SOLO dentro del (tenant, env, sucursal) del JWT — el backend lo acota.
// Aquí no hay UI cross-branch: el dispositivo queda atado a la estación (= una
// sucursal) que resolvió su pairingCode.
//
// Edge cases:
//   - HOST offline → secciones 3-5 deshabilitadas con tooltip "Requiere bridge
//     conectado". La sección 1 sigue visible. (En relay la sección 3 se oculta:
//     no hay loopback que probar.)
//   - HOST outdated → warning naranja pero deja toggles editables (se persisten
//     para cuando el bridge esté actualizado).
//   - relay estación offline → toggles editables; las impresiones se encolan y
//     salen cuando el Bridge HOST vuelva en línea.
//   - localStorage corrupto → el store hace try/catch + reset al default.

import { useCallback, useEffect, useState } from 'react'
import {
  Printer, RefreshCw, CheckCircle2, AlertTriangle, XCircle,
  TestTube, Wallet, ListTree, Settings2, FileText, Trash2,
  Server, Cloud, Plug, Link2, RotateCw, Loader2, Clock, Copy, Check, UploadCloud,
} from 'lucide-react'
import { useBridgeStatus } from '../../hooks/useBridgeStatus.js'
import {
  getPrinters, testPage, kickDrawer, resolveDefaultPrinter, getStatus, printReceipt,
  type BridgePrinter, type BridgeStatus, type CanonicalReceipt,
} from '../../lib/bridge/bridge-client.js'
import {
  resolveStation, listJobs, retryJob, getBridgeIdentity, registerStation,
  type RelayJob, type BridgeRelayIdentity,
} from '../../lib/bridge/relay-client.js'
import { trackRelayJob } from '../../lib/bridge/relay-job-tracker.js'
import {
  usePrintingConfigStore, pushPrintHistory,
  type SurfaceKey, type PaymentMethodKey,
} from '../../lib/bridge/printing-config-store.js'

// ── tipos UI ─────────────────────────────────────────────────────────────────

const SURFACES: ReadonlyArray<{ key: SurfaceKey; title: string; sub: string; maxCopies: number }> = [
  { key: 'barra', title: 'Venta de Barra', sub: '/pos/barra → al cobrar', maxCopies: 3 },
  { key: 'comanda', title: 'Comanda DINE_IN', sub: '/order mesa → al cobrar', maxCopies: 3 },
  { key: 'kds', title: 'KDS / Cocina', sub: 'Al enviar a cocina (fire) — ticket de cocina', maxCopies: 2 },
]

const PAYMENT_METHODS: ReadonlyArray<{ key: PaymentMethodKey; label: string }> = [
  { key: 'CASH', label: 'Efectivo (CASH)' },
  { key: 'CARD_DEBIT', label: 'Tarjeta débito (CARD_DEBIT)' },
  { key: 'CARD_CREDIT', label: 'Tarjeta crédito (CARD_CREDIT)' },
  { key: 'TRANSFER', label: 'Transferencia (TRANSFER)' },
  { key: 'MEAL_VOUCHER', label: 'Vale (MEAL_VOUCHER)' },
  { key: 'OTHER', label: 'Otro (OTHER)' },
]

// ── componente raíz ──────────────────────────────────────────────────────────

export default function PrintingSettingsPage() {
  const [toast, setToast] = useState<{ msg: string; kind: 'ok' | 'err' } | null>(null)
  const flash = (msg: string, kind: 'ok' | 'err' = 'ok') => {
    setToast({ msg, kind })
    setTimeout(() => setToast(null), 2800)
  }

  const mode = usePrintingConfigStore((s) => s.bridge.mode)
  const isRelay = mode === 'relay'
  const { status, refresh, lastCheckedAt } = useBridgeStatus()
  const isOnline = status?.state === 'online'
  const isOutdated = status?.state === 'outdated'
  // Pruebas (sección 3) habla al loopback (listar impresoras / test directo) → SOLO host.
  const canTest = isOnline
  // Toggles (4 y 5) se pueden editar mientras el destino esté "vivo" (host online u
  // outdated, o relay con estación online) — se persisten para cuando imprima.
  const canConfigure = isOnline || isOutdated || status?.state === 'relay-online'

  return (
    <div className="dhk8-print-cfg">
      <h1 className="dhk8-print-cfg__h1"><Printer size={18} /> Impresión</h1>
      <p className="dhk8-print-cfg__lead">
        Configura cómo imprime esta terminal. Los cambios se guardan solo aquí
        (localStorage). Puede imprimir en una impresora conectada a <strong>este equipo</strong>
        (<code>127.0.0.1:9101</code>) o <strong>vía nube</strong>: encolar los tickets para
        que la estación con el Bridge los imprima (ideal para tablets en WiFi).
      </p>

      <BridgeDetector status={status} lastCheckedAt={lastCheckedAt} onRefresh={() => void refresh()} />

      <ConnectionSection onRefresh={() => void refresh()} onFlash={flash} />

      {!isRelay && (
        <PruebasSection enabled={canTest} disabledReason={disabledReason(status)} onFlash={flash} />
      )}

      <AutoPrintSection enabled={canConfigure} disabledReason={disabledReason(status)} />

      <DrawerMatrixSection enabled={canConfigure} disabledReason={disabledReason(status)} />

      <HistorySection />

      {toast && <div className={`dhk8-print-cfg__toast dhk8-print-cfg__toast--${toast.kind}`}>{toast.msg}</div>}
      <style>{styles}</style>
    </div>
  )
}

function disabledReason(status: ReturnType<typeof useBridgeStatus>['status']): string | null {
  if (!status) return 'Detectando…'
  if (status.state === 'offline') return 'Requiere bridge conectado'
  if (status.state === 'outdated') return 'Bridge desactualizado — algunas funciones no disponibles'
  if (status.state === 'relay-offline') return 'La estación está fuera de línea'
  return null
}

// ── Sección 1: detector ──────────────────────────────────────────────────────

interface BridgeDetectorProps {
  status: ReturnType<typeof useBridgeStatus>['status']
  lastCheckedAt: string | null
  onRefresh: () => void
}

function BridgeDetector({ status, lastCheckedAt, onRefresh }: BridgeDetectorProps) {
  let chip: React.ReactNode = null
  let detail: React.ReactNode = null

  if (!status) {
    chip = <span className="dhk8-print-cfg__chip dhk8-print-cfg__chip--neutral"><RefreshCw size={12} className="dhk8-print-cfg__spin" /> Detectando…</span>
    detail = <p className="dhk8-print-cfg__note">Probando conexión al bridge en 127.0.0.1:9101.</p>
  } else if (status.state === 'online') {
    chip = (
      <span className="dhk8-print-cfg__chip dhk8-print-cfg__chip--ok">
        <CheckCircle2 size={12} /> Bridge conectado
      </span>
    )
    detail = (
      <dl className="dhk8-print-cfg__kv">
        <dt>Servicio</dt><dd>{status.version.name}</dd>
        <dt>Versión</dt><dd>{status.version.version} <span className="dhk8-print-cfg__muted">· {status.version.channel}</span></dd>
        <dt>Plataforma</dt><dd>{status.version.platform}{status.version.os ? ` · ${status.version.os}` : ''}</dd>
        <dt>Capacidades</dt>
        <dd>
          {status.version.capabilities.length === 0
            ? <span className="dhk8-print-cfg__muted">sin capabilities reportadas</span>
            : status.version.capabilities.map((c) => <span key={c} className="dhk8-print-cfg__tag">{c}</span>)}
        </dd>
      </dl>
    )
  } else if (status.state === 'relay-online') {
    chip = (
      <span className="dhk8-print-cfg__chip dhk8-print-cfg__chip--ok">
        <Cloud size={12} /> Estación en línea
      </span>
    )
    detail = (
      <dl className="dhk8-print-cfg__kv">
        <dt>Modo</dt><dd>Vía nube</dd>
        <dt>Estación</dt><dd>{status.stationName}</dd>
        <dt>Heartbeat</dt>
        <dd>{status.lastSeenAt
          ? new Date(status.lastSeenAt).toLocaleTimeString('es-MX')
          : <span className="dhk8-print-cfg__muted">recién ahora</span>}</dd>
      </dl>
    )
  } else if (status.state === 'relay-offline') {
    chip = (
      <span className="dhk8-print-cfg__chip dhk8-print-cfg__chip--err">
        <Cloud size={12} /> Estación fuera de línea
      </span>
    )
    detail = (
      <p className="dhk8-print-cfg__note dhk8-print-cfg__note--err">
        {status.stationName ? <>La estación <strong>{status.stationName}</strong> no responde. </> : null}
        Verifica que el equipo con el Bridge esté encendido y conectado a internet.
        Las impresiones se encolarán y saldrán cuando vuelva en línea.
        <br />Razón: {status.reason}
      </p>
    )
  } else if (status.state === 'outdated') {
    chip = (
      <span className="dhk8-print-cfg__chip dhk8-print-cfg__chip--warn">
        <AlertTriangle size={12} /> Bridge desactualizado
      </span>
    )
    detail = (
      <p className="dhk8-print-cfg__note dhk8-print-cfg__note--warn">
        Se detectó el servicio en este equipo, pero no expone el endpoint <code>/v1/version</code>.
        Solicita al equipo técnico el rebuild del bridge (<code>NintaiGO.Bridge.exe</code>).
        <br />Razón: {status.reason}
      </p>
    )
  } else {
    chip = (
      <span className="dhk8-print-cfg__chip dhk8-print-cfg__chip--err">
        <XCircle size={12} /> No hay bridge
      </span>
    )
    detail = (
      <p className="dhk8-print-cfg__note dhk8-print-cfg__note--err">
        No se pudo conectar al bridge. Instala <code>NintaiGO Bridge</code> en este equipo
        o reinicia el servicio (Servicios de Windows → NintaiGO Bridge → Iniciar).
        <br />Razón: {status.reason}
      </p>
    )
  }

  return (
    <section className="dhk8-print-cfg__section">
      <header className="dhk8-print-cfg__section-head">
        <h2 className="dhk8-print-cfg__h2">1. Estado del bridge</h2>
        <button type="button" className="dhk8-print-cfg__ghost-btn" onClick={onRefresh} aria-label="Reintentar detección">
          <RefreshCw size={12} /> Reintentar
        </button>
      </header>
      <div className="dhk8-print-cfg__panel">
        <div className="dhk8-print-cfg__chip-row">
          {chip}
          {lastCheckedAt && (
            <span className="dhk8-print-cfg__muted dhk8-print-cfg__small">
              Última verificación: {new Date(lastCheckedAt).toLocaleTimeString('es-MX')}
            </span>
          )}
        </div>
        {detail}
      </div>
    </section>
  )
}

// ── Sección 2: conexión del bridge (HOST / Vía nube) ─────────────────────────

function ConnectionSection({ onRefresh, onFlash }: { onRefresh: () => void; onFlash: (msg: string, kind?: 'ok' | 'err') => void }) {
  const bridge = usePrintingConfigStore((s) => s.bridge)
  const setBridge = usePrintingConfigStore((s) => s.setBridge)
  const isRelay = bridge.mode === 'relay'

  return (
    <section className="dhk8-print-cfg__section">
      <h2 className="dhk8-print-cfg__h2">2. Conexión del Bridge</h2>
      <p className="dhk8-print-cfg__lead">
        Elige cómo imprime esta terminal. Usa <strong>Este equipo</strong> si la impresora
        está conectada por USB aquí, o <strong>Vía nube</strong> para encolar los tickets
        y que la estación con el Bridge los imprima (perfecto para tablets en WiFi — sin
        configurar el router).
      </p>
      <div className="dhk8-print-cfg__panel">
        <div className="dhk8-print-cfg__seg" role="radiogroup" aria-label="Modo de conexión del bridge">
          <SegOption
            active={!isRelay}
            icon={<Server size={15} />}
            title="Este equipo (USB local)"
            sub="El Bridge corre aquí · 127.0.0.1"
            onClick={() => { setBridge({ mode: 'host' }); onRefresh() }}
          />
          <SegOption
            active={isRelay}
            icon={<Cloud size={15} />}
            title="Vía nube"
            sub="Encola e imprime en la estación"
            onClick={() => { setBridge({ mode: 'relay' }); onRefresh() }}
          />
        </div>

        {!isRelay ? (
          <HostConnectionBody onRefresh={onRefresh} />
        ) : (
          <RelayConnectionBody onRefresh={onRefresh} onFlash={onFlash} />
        )}
      </div>
    </section>
  )
}

function SegOption({ active, icon, title, sub, onClick }: {
  active: boolean
  icon: React.ReactNode
  title: string
  sub: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className={`dhk8-print-cfg__seg-opt${active ? ' dhk8-print-cfg__seg-opt--active' : ''}`}
      onClick={onClick}
      role="radio"
      aria-checked={active}
    >
      <span className="dhk8-print-cfg__seg-icon">{icon}</span>
      <span className="dhk8-print-cfg__seg-text">
        <span className="dhk8-print-cfg__seg-title">{title}</span>
        <span className="dhk8-print-cfg__seg-sub">{sub}</span>
      </span>
    </button>
  )
}

// ── HOST (loopback): probar conexión al bridge local ─────────────────────────

function HostConnectionBody({ onRefresh }: { onRefresh: () => void }) {
  const [probe, setProbe] = useState<{ state: 'idle' | 'testing' } | { state: 'done'; result: BridgeStatus }>({ state: 'idle' })

  async function handleTest() {
    setProbe({ state: 'testing' })
    try {
      const result = await getStatus()
      setProbe({ state: 'done', result })
    } catch (e) {
      setProbe({ state: 'done', result: { state: 'offline', reason: e instanceof Error ? e.message : 'Error desconocido' } })
    }
    onRefresh()
  }

  return (
    <>
      <div className="dhk8-print-cfg__actions">
        <button
          type="button"
          className="dhk8-print-cfg__btn"
          disabled={probe.state === 'testing'}
          onClick={() => void handleTest()}
        >
          <Plug size={14} /> {probe.state === 'testing' ? 'Probando…' : 'Probar conexión'}
        </button>
      </div>
      {probe.state === 'done' && <ProbeResult result={probe.result} />}
      <p className="dhk8-print-cfg__note">
        La impresora debe estar conectada por USB a este equipo, con el Bridge
        de NintaiGO instalado y corriendo (<code>127.0.0.1:9101</code>).
      </p>
      <HostCloudPublish />
    </>
  )
}

// ── HOST: publicar esta estación en la nube (registro para impresión por relay) ──
// El HOST corre el bridge + la impresora. Esta sección lee el identity del bridge
// LOCAL (loopback) y registra la estación en la nube, para que tablets/celulares del
// MISMO restaurante encolen tickets a esta impresora (Print Relay). El HOST sigue
// imprimiendo por loopback (rápido); esto es ADITIVO. En un satélite getBridgeIdentity
// falla (no hay bridge local) → mostramos el hint para que use "Vía nube" con el código.
function HostCloudPublish() {
  const published = usePrintingConfigStore((s) => s.bridge.publishedStation)
  const setBridge = usePrintingConfigStore((s) => s.setBridge)
  const [identity, setIdentity] = useState<BridgeRelayIdentity | null>(null)
  const [probing, setProbing] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  // Al montar: intenta leer el identity del bridge local (loopback). Si falla, no es
  // el HOST (o el bridge no tiene relay) → dejamos el hint; el botón reintenta.
  useEffect(() => {
    let alive = true
    void (async () => {
      try {
        const id = await getBridgeIdentity()
        if (alive) setIdentity(id)
      } catch {
        /* no es el HOST o bridge sin relay — UI cae a estado neutro */
      } finally {
        if (alive) setProbing(false)
      }
    })()
    return () => { alive = false }
  }, [])

  async function handlePublish() {
    setBusy(true); setError(null)
    try {
      const id = identity ?? (await getBridgeIdentity())
      setIdentity(id)
      if (id.relayEnabled === false) {
        throw new Error('El Bridge corre pero sin relay (falta NINTAIGO_BRIDGE_RELAY_URL). Habilítalo y reinicia el Bridge.')
      }
      const station = await registerStation({
        name: id.suggestedName || 'Estación principal',
        pairingCode: id.pairingCode,
        pollSecret: id.pollSecret,
      })
      setBridge({ publishedStation: { stationId: station.stationId, name: station.name, pairingCode: id.pairingCode } })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo publicar la estación')
    } finally {
      setBusy(false)
    }
  }

  async function copyCode(code: string) {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch { /* clipboard bloqueado — el código se ve igual en pantalla */ }
  }

  return (
    <div className="dhk8-print-cfg__cloudpub">
      <div className="dhk8-print-cfg__cloudpub-head">
        <span className="dhk8-print-cfg__cloudpub-icon"><Cloud size={16} /></span>
        <div className="dhk8-print-cfg__cloudpub-text">
          <span className="dhk8-print-cfg__cloudpub-title">Imprimir desde tablets y celulares (vía nube)</span>
          <span className="dhk8-print-cfg__cloudpub-sub">
            Publica esta estación para que otros dispositivos del mismo restaurante
            encolen sus tickets a esta impresora — sin tocar el router.
          </span>
        </div>
      </div>

      {published ? (
        <div className="dhk8-print-cfg__note dhk8-print-cfg__note--ok">
          <CheckCircle2 size={14} style={{ verticalAlign: '-3px', marginRight: 6 }} />
          Publicada como <strong>{published.name}</strong>. Da este código a las tablets/celulares:
          <div className="dhk8-print-cfg__paircode-row">
            <code className="dhk8-print-cfg__paircode">{published.pairingCode}</code>
            <button type="button" className="dhk8-print-cfg__ghost-btn" onClick={() => void copyCode(published.pairingCode)}>
              {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? 'Copiado' : 'Copiar'}
            </button>
          </div>
          <span className="dhk8-print-cfg__cloudpub-howto">
            En la tablet/celular: <strong>Ajustes › Impresión › Vía nube</strong> → teclea este código → Vincular.
          </span>
          <div className="dhk8-print-cfg__actions" style={{ marginTop: 8 }}>
            <button type="button" className="dhk8-print-cfg__ghost-btn" disabled={busy} onClick={() => void handlePublish()}>
              {busy ? <Loader2 size={12} className="dhk8-print-cfg__spin" /> : <RotateCw size={12} />} Volver a publicar
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="dhk8-print-cfg__actions">
            <button type="button" className="dhk8-print-cfg__btn" disabled={busy || probing} onClick={() => void handlePublish()}>
              {busy ? <Loader2 size={14} className="dhk8-print-cfg__spin" /> : <UploadCloud size={14} />}
              {busy ? 'Publicando…' : probing ? 'Detectando Bridge…' : 'Publicar esta estación en la nube'}
            </button>
          </div>
          {identity && (
            <p className="dhk8-print-cfg__note">
              Bridge detectado en este equipo. Código de esta estación: <code>{identity.pairingCode}</code>.
              {identity.relayEnabled === false && ' ⚠️ El Bridge no tiene relay habilitado todavía.'}
            </p>
          )}
          {!identity && !probing && !error && (
            <p className="dhk8-print-cfg__note">
              Esto se hace en el equipo donde está el Bridge y la impresora. Si estás en una
              tablet/celular, usa <strong>Vía nube</strong> arriba y teclea el código que te dé el equipo principal.
            </p>
          )}
        </>
      )}
      {error && (
        <p className="dhk8-print-cfg__note dhk8-print-cfg__note--err">
          <XCircle size={14} style={{ verticalAlign: '-3px', marginRight: 6 }} />
          {error}
        </p>
      )}
    </div>
  )
}

function ProbeResult({ result }: { result: BridgeStatus }) {
  if (result.state === 'online') {
    return (
      <p className="dhk8-print-cfg__note dhk8-print-cfg__note--ok">
        <CheckCircle2 size={14} style={{ verticalAlign: '-3px', marginRight: 6 }} />
        Bridge en línea — {result.version.name} v{result.version.version}
        <span className="dhk8-print-cfg__muted"> · {result.version.platform}</span>
      </p>
    )
  }
  if (result.state === 'outdated') {
    return (
      <p className="dhk8-print-cfg__note dhk8-print-cfg__note--warn">
        <AlertTriangle size={14} style={{ verticalAlign: '-3px', marginRight: 6 }} />
        Bridge desactualizado — {result.reason}
      </p>
    )
  }
  if (result.state === 'relay-online') {
    return (
      <p className="dhk8-print-cfg__note dhk8-print-cfg__note--ok">
        <CheckCircle2 size={14} style={{ verticalAlign: '-3px', marginRight: 6 }} />
        Estación en línea — {result.stationName}
      </p>
    )
  }
  if (result.state === 'relay-offline') {
    return (
      <p className="dhk8-print-cfg__note dhk8-print-cfg__note--err">
        <XCircle size={14} style={{ verticalAlign: '-3px', marginRight: 6 }} />
        Estación fuera de línea — {result.reason}
      </p>
    )
  }
  return (
    <p className="dhk8-print-cfg__note dhk8-print-cfg__note--err">
      <XCircle size={14} style={{ verticalAlign: '-3px', marginRight: 6 }} />
      Sin conexión — {result.reason}
    </p>
  )
}

// ── Vía nube (relay): vincular por código + panel de estación/jobs ───────────

function RelayConnectionBody({ onRefresh, onFlash }: { onRefresh: () => void; onFlash: (msg: string, kind?: 'ok' | 'err') => void }) {
  const bridge = usePrintingConfigStore((s) => s.bridge)
  const setBridge = usePrintingConfigStore((s) => s.setBridge)
  const linked = !!bridge.stationId

  if (!linked) {
    return <RelayPairing onRefresh={onRefresh} onFlash={onFlash} />
  }
  return (
    <RelayStationPanel
      stationId={bridge.stationId as string}
      stationName={bridge.stationName ?? 'Estación'}
      onUnlink={() => { setBridge({ stationId: null, stationName: null }); onRefresh() }}
      onFlash={onFlash}
    />
  )
}

function RelayPairing({ onRefresh, onFlash }: { onRefresh: () => void; onFlash: (msg: string, kind?: 'ok' | 'err') => void }) {
  const setBridge = usePrintingConfigStore((s) => s.setBridge)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const trimmed = code.trim()
  const canLink = trimmed.length >= 4 && !busy

  async function handleLink() {
    if (!canLink) return
    setBusy(true)
    setError(null)
    try {
      const { stationId, name } = await resolveStation(trimmed)
      setBridge({ stationId, stationName: name })
      onFlash(`Vinculado a "${name}"`)
      onRefresh()
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'No se pudo vincular'
      // Mensaje accionable cuando el backend responde 404 (código equivocado / otro tenant/env).
      setError(/404|not.?found/i.test(msg)
        ? 'Código no encontrado. Verifica que sea el de esta cuenta y entorno (pruebas/vivo).'
        : msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="dhk8-print-cfg__fields">
      <label className="dhk8-print-cfg__field">
        <span className="dhk8-print-cfg__field-label">Código de estación</span>
        {/* type="text" a propósito (NO numérico) — el código es alfanumérico
            (XXXX-XXXX) y un input numérico abría el teclado equivocado en iPhone. */}
        <input
          type="text"
          className="dhk8-print-cfg__input dhk8-print-cfg__input--code"
          inputMode="text"
          placeholder="XXXX-XXXX"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === 'Enter') void handleLink() }}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          maxLength={12}
          aria-label="Código de estación"
        />
        <span className="dhk8-print-cfg__field-hint">
          Lo muestra el Bridge en el equipo principal (Ajustes › Impresión › Este equipo).
          El mismo código resuelve solo dentro de esta cuenta y entorno.
        </span>
      </label>
      <div className="dhk8-print-cfg__actions">
        <button type="button" className="dhk8-print-cfg__btn" disabled={!canLink} onClick={() => void handleLink()}>
          {busy ? <Loader2 size={14} className="dhk8-print-cfg__spin" /> : <Link2 size={14} />}
          {busy ? 'Vinculando…' : 'Vincular'}
        </button>
      </div>
      {error && (
        <p className="dhk8-print-cfg__note dhk8-print-cfg__note--err">
          <XCircle size={14} style={{ verticalAlign: '-3px', marginRight: 6 }} />
          {error}
        </p>
      )}
    </div>
  )
}

/** Ticket de prueba mínimo para "Probar impresión" en el panel de la estación.
 *  No requiere una venta — solo verifica que el job cruza la nube y sale el papel. */
function buildRelayTestReceipt(stationName: string): CanonicalReceipt {
  return {
    encoding: 'cp858',
    width: 42,
    header: [
      { text: 'NINTAIGO', align: 'center', bold: true, size: 'double' },
      { text: 'Prueba de impresion', align: 'center' },
    ],
    footer: [
      { text: `Estacion: ${stationName}`, align: 'center' },
      { text: new Date().toLocaleString('es-MX'), align: 'center' },
      { text: 'Via nube (Print Relay)', align: 'center' },
    ],
    cut: 'partial',
  }
}

function RelayStationPanel({ stationId, stationName, onUnlink, onFlash }: {
  stationId: string
  stationName: string
  onUnlink: () => void
  onFlash: (msg: string, kind?: 'ok' | 'err') => void
}) {
  const { status } = useBridgeStatus()
  const online = status?.state === 'relay-online'
  const lastSeenAt = (status?.state === 'relay-online' || status?.state === 'relay-offline') ? status.lastSeenAt : null

  const [jobs, setJobs] = useState<RelayJob[] | null>(null)
  const [jobsError, setJobsError] = useState<string | null>(null)
  const [retrying, setRetrying] = useState<string | null>(null)
  const [testing, setTesting] = useState(false)

  const loadJobs = useCallback(async (signal?: AbortSignal) => {
    try {
      const list = await listJobs(stationId, 10, signal)
      setJobs(list)
      setJobsError(null)
    } catch (e) {
      if (signal?.aborted) return
      setJobsError(e instanceof Error ? e.message : 'No se pudieron cargar los trabajos')
    }
  }, [stationId])

  // "Probar impresión": encola un ticket mínimo a ESTA estación y sigue el job
  // por el tracker global (enviado→imprimiendo→impreso/falló como toast). Sirve
  // para verificar el riel completo sin cerrar una venta. printReceipt en mode
  // 'relay' devuelve { jobId } apenas entra a la cola.
  async function handleTestPrint() {
    setTesting(true)
    try {
      const r = await printReceipt(buildRelayTestReceipt(stationName), { kind: 'receipt' })
      if (r.jobId) {
        trackRelayJob(r.jobId, 'Prueba de impresión', 'receipt')
        onFlash('Trabajo de prueba enviado a la estación')
        await loadJobs()
      } else {
        onFlash('No se obtuvo el id del trabajo', 'err')
      }
    } catch (e) {
      onFlash(e instanceof Error ? e.message : 'No se pudo enviar la prueba', 'err')
    } finally {
      setTesting(false)
    }
  }

  // Carga inicial + refresco cada 8s (los estados se mueven al imprimir/reintentar).
  useEffect(() => {
    const ctrl = new AbortController()
    void loadJobs(ctrl.signal)
    const t = setInterval(() => { void loadJobs() }, 8_000)
    return () => { ctrl.abort(); clearInterval(t) }
  }, [loadJobs])

  async function handleRetry(id: string) {
    setRetrying(id)
    try {
      await retryJob(id)
      onFlash('Reimpresión solicitada')
      await loadJobs()
    } catch (e) {
      onFlash(e instanceof Error ? e.message : 'No se pudo reimprimir', 'err')
    } finally {
      setRetrying(null)
    }
  }

  return (
    <div className="dhk8-print-cfg__relay">
      <div className="dhk8-print-cfg__relay-head">
        <div className="dhk8-print-cfg__relay-station">
          <Cloud size={16} className="dhk8-print-cfg__relay-station-icon" />
          <div className="dhk8-print-cfg__relay-station-text">
            <span className="dhk8-print-cfg__relay-station-name">{stationName}</span>
            <span className={`dhk8-print-cfg__relay-state dhk8-print-cfg__relay-state--${online ? 'on' : 'off'}`}>
              <span className="dhk8-print-cfg__relay-dot" />
              {online ? 'En línea' : 'Fuera de línea'}
              {lastSeenAt && (
                <span className="dhk8-print-cfg__muted dhk8-print-cfg__small">
                  {' · '}<Clock size={10} style={{ verticalAlign: '-1px' }} /> {new Date(lastSeenAt).toLocaleTimeString('es-MX')}
                </span>
              )}
            </span>
          </div>
        </div>
        <button type="button" className="dhk8-print-cfg__ghost-btn" onClick={onUnlink}>
          <Link2 size={12} /> Desvincular
        </button>
      </div>

      {!online && (
        <p className="dhk8-print-cfg__note dhk8-print-cfg__note--warn">
          La estación no está reportando. Las impresiones se encolarán y saldrán
          en cuanto el equipo con el Bridge vuelva en línea.
        </p>
      )}

      <div className="dhk8-print-cfg__relay-jobs-head">
        <span className="dhk8-print-cfg__small dhk8-print-cfg__muted">Trabajos recientes</span>
        <div className="dhk8-print-cfg__relay-jobs-actions">
          <button type="button" className="dhk8-print-cfg__ghost-btn" disabled={testing} onClick={() => void handleTestPrint()}>
            {testing ? <Loader2 size={12} className="dhk8-print-cfg__spin" /> : <TestTube size={12} />} Probar impresión
          </button>
          <button type="button" className="dhk8-print-cfg__ghost-btn" onClick={() => void loadJobs()}>
            <RefreshCw size={12} /> Actualizar
          </button>
        </div>
      </div>

      {jobsError && <p className="dhk8-print-cfg__note dhk8-print-cfg__note--err">{jobsError}</p>}
      {jobs === null && !jobsError && (
        <p className="dhk8-print-cfg__note"><Loader2 size={14} className="dhk8-print-cfg__spin" style={{ verticalAlign: '-3px', marginRight: 6 }} /> Cargando trabajos…</p>
      )}
      {jobs !== null && jobs.length === 0 && (
        <p className="dhk8-print-cfg__note">Aún no hay trabajos enviados a esta estación.</p>
      )}
      {jobs !== null && jobs.length > 0 && (
        <ul className="dhk8-print-cfg__joblist">
          {jobs.map((j) => (
            <RelayJobRow key={j.id} job={j} retrying={retrying === j.id} onRetry={() => void handleRetry(j.id)} />
          ))}
        </ul>
      )}
    </div>
  )
}

/** Etiqueta + clase visual por estado del job del relay. */
function jobStatusView(job: RelayJob): { label: string; tone: 'pending' | 'progress' | 'ok' | 'retry' | 'fail' } {
  const attemptsTxt = job.maxAttempts ? ` (${job.attempts}/${job.maxAttempts})` : ''
  switch (job.status) {
    case 'pending':
      return { label: job.attempts > 0 ? `Reintentando${attemptsTxt}` : 'Enviado', tone: job.attempts > 0 ? 'retry' : 'pending' }
    case 'claimed':
      return { label: 'Imprimiendo', tone: 'progress' }
    case 'done':
      return { label: 'Impreso', tone: 'ok' }
    case 'failed':
      return { label: `Falló${attemptsTxt}`, tone: 'fail' }
    default:
      return { label: String(job.status), tone: 'pending' }
  }
}

function RelayJobRow({ job, retrying, onRetry }: { job: RelayJob; retrying: boolean; onRetry: () => void }) {
  const view = jobStatusView(job)
  const kindLabel = job.kind ? String(job.kind) : 'job'
  // Reimprimir disponible si falló o ya terminó (re-encola el mismo trabajo).
  const canRetry = job.status === 'failed' || job.status === 'done'
  return (
    <li className={`dhk8-print-cfg__job dhk8-print-cfg__job--${view.tone}`}>
      <span className="dhk8-print-cfg__job-kind"><Settings2 size={11} /> {kindLabel}</span>
      <span className="dhk8-print-cfg__job-when">
        {job.createdAt ? new Date(job.createdAt).toLocaleTimeString('es-MX') : ''}
      </span>
      <span className={`dhk8-print-cfg__job-status dhk8-print-cfg__job-status--${view.tone}`}>{view.label}</span>
      {canRetry && (
        <button type="button" className="dhk8-print-cfg__job-retry" disabled={retrying} onClick={onRetry}>
          {retrying ? <Loader2 size={11} className="dhk8-print-cfg__spin" /> : <RotateCw size={11} />} Reimprimir
        </button>
      )}
      {job.lastError && <span className="dhk8-print-cfg__job-err">{job.lastError}</span>}
    </li>
  )
}

// ── Sección 3: pruebas + impresoras ──────────────────────────────────────────

interface PruebasProps {
  enabled: boolean
  disabledReason: string | null
  onFlash: (msg: string, kind?: 'ok' | 'err') => void
}

function PruebasSection({ enabled, disabledReason, onFlash }: PruebasProps) {
  const [busy, setBusy] = useState<null | 'test' | 'drawer' | 'list'>(null)
  const [printers, setPrinters] = useState<BridgePrinter[] | null>(null)
  const [printerError, setPrinterError] = useState<string | null>(null)
  const defaultPrinter = usePrintingConfigStore((s) => s.defaultPrinter)
  const setDefaultPrinter = usePrintingConfigStore((s) => s.setDefaultPrinter)

  // Al conectarse el bridge por primera vez (enabled flip false→true) cargamos
  // la lista de impresoras automáticamente; si no hay defaultPrinter elegido,
  // adoptamos el `isDefault: true` reportado por Windows.
  useEffect(() => {
    if (!enabled) return
    let alive = true
    void (async () => {
      try {
        const list = await getPrinters()
        if (!alive) return
        setPrinters(list)
        setPrinterError(null)
        if (!defaultPrinter && list.length > 0) {
          const winDefault = list.find((p) => p.isDefault === true) ?? list[0]
          if (winDefault) setDefaultPrinter(winDefault.name)
        }
      } catch (e) {
        if (!alive) return
        setPrinterError(e instanceof Error ? e.message : 'No se pudo listar impresoras')
      }
    })()
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  async function handleTestPage() {
    setBusy('test')
    try {
      // Usa la impresora seleccionada en el dropdown (defaultPrinter del store);
      // si null, testPage() resuelve la default del bridge.
      await testPage({ printer: defaultPrinter })
      pushPrintHistory('test-page', 'ok')
      onFlash('Página de prueba enviada')
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Error desconocido'
      pushPrintHistory('test-page', 'error', msg)
      onFlash(`No se pudo imprimir: ${msg}`, 'err')
    } finally { setBusy(null) }
  }

  async function handleKickDrawer() {
    setBusy('drawer')
    try {
      const printer = await resolveDefaultPrinter(defaultPrinter)
      if (!printer) throw new Error('No hay impresora configurada')
      await kickDrawer({ printer })
      pushPrintHistory('drawer', 'ok')
      onFlash('Cajón abierto')
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Error desconocido'
      pushPrintHistory('drawer', 'error', msg)
      onFlash(`No se pudo abrir el cajón: ${msg}`, 'err')
    } finally { setBusy(null) }
  }

  async function handleListPrinters() {
    setBusy('list')
    try {
      const list = await getPrinters()
      setPrinters(list)
      setPrinterError(null)
      onFlash(`${list.length} impresora${list.length === 1 ? '' : 's'} detectada${list.length === 1 ? '' : 's'}`)
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Error desconocido'
      setPrinterError(msg)
      onFlash(`No se pudo listar: ${msg}`, 'err')
    } finally { setBusy(null) }
  }

  const disabled = !enabled

  return (
    <section className="dhk8-print-cfg__section" aria-disabled={disabled}>
      <h2 className="dhk8-print-cfg__h2">3. Pruebas e impresora</h2>
      <div className="dhk8-print-cfg__panel" title={disabledReason ?? undefined}>
        <div className="dhk8-print-cfg__actions">
          <button type="button" className="dhk8-print-cfg__btn" disabled={disabled || busy !== null} onClick={() => void handleTestPage()}>
            <TestTube size={14} /> {busy === 'test' ? 'Imprimiendo…' : 'Imprimir página de prueba'}
          </button>
          <button type="button" className="dhk8-print-cfg__btn" disabled={disabled || busy !== null} onClick={() => void handleKickDrawer()}>
            <Wallet size={14} /> {busy === 'drawer' ? 'Abriendo…' : 'Abrir cajón de efectivo'}
          </button>
          <button type="button" className="dhk8-print-cfg__btn" disabled={disabled || busy !== null} onClick={() => void handleListPrinters()}>
            <ListTree size={14} /> {busy === 'list' ? 'Consultando…' : 'Listar impresoras detectadas'}
          </button>
        </div>
        {disabled && disabledReason && (
          <p className="dhk8-print-cfg__note">{disabledReason}</p>
        )}
        {printerError && <p className="dhk8-print-cfg__note dhk8-print-cfg__note--err">{printerError}</p>}
        {printers !== null && (
          <PrinterList
            printers={printers}
            selected={defaultPrinter}
            onSelect={(name) => setDefaultPrinter(name)}
            disabled={disabled}
          />
        )}
      </div>
    </section>
  )
}

function PrinterList({ printers, selected, onSelect, disabled }: {
  printers: BridgePrinter[]
  selected: string | null
  onSelect: (name: string) => void
  disabled: boolean
}) {
  if (printers.length === 0) {
    return <p className="dhk8-print-cfg__note">Sin impresoras detectadas en Windows.</p>
  }
  return (
    <div className="dhk8-print-cfg__printers">
      <div className="dhk8-print-cfg__small dhk8-print-cfg__muted">
        Impresora usada por auto-print y "abrir cajón". El bridge requiere especificarla en cada solicitud.
      </div>
      <ul className="dhk8-print-cfg__printer-list">
        {printers.map((p) => {
          const isSel = (selected ?? '') === p.name
          return (
            <li key={p.name}>
              <button
                type="button"
                className={`dhk8-print-cfg__printer-row${isSel ? ' dhk8-print-cfg__printer-row--sel' : ''}`}
                onClick={() => onSelect(p.name)}
                disabled={disabled}
                aria-pressed={isSel}
              >
                <span className="dhk8-print-cfg__printer-name">{p.name}</span>
                <span className="dhk8-print-cfg__printer-tags">
                  {p.isDefault && <span className="dhk8-print-cfg__tag dhk8-print-cfg__tag--win">Windows default</span>}
                  {p.isOnline === false && <span className="dhk8-print-cfg__tag dhk8-print-cfg__tag--err">Offline</span>}
                  {isSel && <span className="dhk8-print-cfg__tag dhk8-print-cfg__tag--sel">Seleccionada</span>}
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

// ── Sección 3: toggles auto-print ────────────────────────────────────────────

function AutoPrintSection({ enabled, disabledReason }: { enabled: boolean; disabledReason: string | null }) {
  const autoPrint = usePrintingConfigStore((s) => s.autoPrint)
  const setAutoPrint = usePrintingConfigStore((s) => s.setAutoPrint)

  return (
    <section className="dhk8-print-cfg__section">
      <h2 className="dhk8-print-cfg__h2">4. Auto-impresión por superficie</h2>
      <p className="dhk8-print-cfg__lead">
        Cuando una superficie está activada, el POS llama al bridge para imprimir
        automáticamente. Si está desactivada, solo se muestra el modal y la
        impresión queda manual.
      </p>
      <div className="dhk8-print-cfg__cards">
        {SURFACES.map((sf) => {
          const setting = autoPrint[sf.key]
          return (
            <div key={sf.key} className="dhk8-print-cfg__card" title={!enabled ? (disabledReason ?? undefined) : undefined}>
              <header className="dhk8-print-cfg__card-head">
                <div>
                  <h3 className="dhk8-print-cfg__card-title">{sf.title}</h3>
                  <div className="dhk8-print-cfg__card-sub">{sf.sub}</div>
                </div>
                <Switch
                  checked={setting.enabled}
                  onChange={(v) => setAutoPrint(sf.key, { enabled: v })}
                  ariaLabel={`Activar auto-impresión para ${sf.title}`}
                />
              </header>
              <label className={`dhk8-print-cfg__copies${!setting.enabled ? ' dhk8-print-cfg__copies--off' : ''}`}>
                <span>Copias</span>
                <CopiesSpinner
                  value={setting.copies}
                  min={1}
                  max={sf.maxCopies}
                  onChange={(v) => setAutoPrint(sf.key, { copies: v })}
                  disabled={!setting.enabled}
                />
                <span className="dhk8-print-cfg__small dhk8-print-cfg__muted">(máx {sf.maxCopies})</span>
              </label>
            </div>
          )
        })}
      </div>
    </section>
  )
}

function Switch({ checked, onChange, ariaLabel }: { checked: boolean; onChange: (v: boolean) => void; ariaLabel: string }) {
  return (
    <button
      type="button"
      className={`dhk8-print-cfg__switch${checked ? ' dhk8-print-cfg__switch--on' : ''}`}
      onClick={() => onChange(!checked)}
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
    >
      <span className="dhk8-print-cfg__switch-thumb" />
    </button>
  )
}

function CopiesSpinner({ value, min, max, onChange, disabled }: {
  value: number; min: number; max: number; onChange: (v: number) => void; disabled?: boolean
}) {
  const clamp = (n: number) => Math.max(min, Math.min(max, n))
  return (
    <div className="dhk8-print-cfg__spinner" aria-disabled={disabled}>
      <button type="button" className="dhk8-print-cfg__spinner-btn" disabled={disabled || value <= min}
        onClick={() => onChange(clamp(value - 1))} aria-label="Disminuir copias">−</button>
      <span className="dhk8-print-cfg__spinner-val" aria-live="polite">{value}</span>
      <button type="button" className="dhk8-print-cfg__spinner-btn" disabled={disabled || value >= max}
        onClick={() => onChange(clamp(value + 1))} aria-label="Aumentar copias">+</button>
    </div>
  )
}

// ── Sección 4: matriz pago × cajón ───────────────────────────────────────────

function DrawerMatrixSection({ enabled, disabledReason }: { enabled: boolean; disabledReason: string | null }) {
  const drawerOnPayment = usePrintingConfigStore((s) => s.drawerOnPayment)
  const setDrawerOnPayment = usePrintingConfigStore((s) => s.setDrawerOnPayment)
  return (
    <section className="dhk8-print-cfg__section">
      <h2 className="dhk8-print-cfg__h2">5. Apertura de cajón por método de pago</h2>
      <p className="dhk8-print-cfg__lead">
        Cuando se registra un pago con un método marcado, el POS dispara
        <code>POST /v1/drawer/kick</code> en el bridge. Sin efecto si el método
        está desactivado.
      </p>
      <div className="dhk8-print-cfg__panel" title={!enabled ? (disabledReason ?? undefined) : undefined}>
        <table className="dhk8-print-cfg__matrix">
          <thead>
            <tr><th>Método</th><th>Abre cajón</th></tr>
          </thead>
          <tbody>
            {PAYMENT_METHODS.map((m) => (
              <tr key={m.key}>
                <td>{m.label}</td>
                <td>
                  <Switch
                    checked={drawerOnPayment[m.key]}
                    onChange={(v) => setDrawerOnPayment(m.key, v)}
                    ariaLabel={`Abrir cajón con ${m.label}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

// ── Sección 5: historial ─────────────────────────────────────────────────────

function HistorySection() {
  const history = usePrintingConfigStore((s) => s.history)
  const clearHistory = usePrintingConfigStore((s) => s.clearHistory)
  return (
    <section className="dhk8-print-cfg__section">
      <header className="dhk8-print-cfg__section-head">
        <h2 className="dhk8-print-cfg__h2">6. Últimas impresiones</h2>
        {history.length > 0 && (
          <button type="button" className="dhk8-print-cfg__ghost-btn" onClick={clearHistory}>
            <Trash2 size={12} /> Limpiar historial
          </button>
        )}
      </header>
      {history.length === 0 ? (
        <p className="dhk8-print-cfg__note"><FileText size={14} style={{ verticalAlign: '-3px', marginRight: 6 }} /> Aún no hay impresiones registradas en esta terminal.</p>
      ) : (
        <ul className="dhk8-print-cfg__history">
          {history.map((h, i) => (
            <li key={`${h.at}-${i}`} className={`dhk8-print-cfg__history-row dhk8-print-cfg__history-row--${h.result}`}>
              <span className="dhk8-print-cfg__history-when">
                {new Date(h.at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'medium' })}
              </span>
              <span className="dhk8-print-cfg__history-kind"><Settings2 size={11} /> {h.kind}</span>
              <span className={`dhk8-print-cfg__history-result dhk8-print-cfg__history-result--${h.result}`}>
                {h.result === 'ok' ? 'OK' : 'Error'}
              </span>
              {h.error && <span className="dhk8-print-cfg__history-err">{h.error}</span>}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

// ── estilos ──────────────────────────────────────────────────────────────────

const styles = `
  .dhk8-print-cfg { display:flex; flex-direction:column; gap:var(--pos-space-5, 20px); max-width:880px; padding-bottom:var(--pos-space-8, 32px); }
  .dhk8-print-cfg__h1 { display:flex; align-items:center; gap:var(--pos-space-2, 8px); margin:0; font-family:var(--font-display, system-ui); font-size:var(--pos-text-2xl, 24px); font-weight:var(--pos-weight-semibold, 600); color:var(--pos-fg, #E8EFE9); }
  .dhk8-print-cfg__lead { margin:0; font-size:var(--pos-text-sm, 13px); color:var(--pos-fg-secondary, #B6BFB8); line-height:1.5; }
  .dhk8-print-cfg__lead code { font-family:var(--font-mono, ui-monospace, monospace); font-size:var(--pos-text-xs, 12px); padding:1px 6px; background:var(--pos-canvas, #0F1411); border:1px solid var(--pos-separator, rgba(255,255,255,.07)); border-radius:var(--pos-radius-md, 8px); }
  .dhk8-print-cfg__section { display:flex; flex-direction:column; gap:var(--pos-space-3, 12px); }
  .dhk8-print-cfg__section-head { display:flex; justify-content:space-between; align-items:center; gap:var(--pos-space-2, 8px); }
  .dhk8-print-cfg__h2 { margin:0; font-size:var(--pos-text-sm, 13px); letter-spacing:var(--pos-tracking-widest, .14em); text-transform:uppercase; color:var(--pos-fg-secondary, #B6BFB8); font-weight:var(--pos-weight-medium, 500); }
  .dhk8-print-cfg__panel { padding:var(--pos-space-4, 16px); background:var(--pos-surface, #161C18); border:1px solid var(--pos-separator, rgba(255,255,255,.07)); border-radius:var(--pos-radius-lg, 12px); display:flex; flex-direction:column; gap:var(--pos-space-3, 12px); }
  .dhk8-print-cfg__section[aria-disabled="true"] .dhk8-print-cfg__panel { opacity:.62; }

  .dhk8-print-cfg__chip-row { display:flex; flex-wrap:wrap; align-items:center; gap:var(--pos-space-2, 8px); }
  .dhk8-print-cfg__chip { display:inline-flex; align-items:center; gap:6px; padding:4px 10px; font-size:var(--pos-text-xs, 12px); border-radius:var(--pos-radius-pill, 999px); border:1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); font-weight:var(--pos-weight-medium, 500); }
  .dhk8-print-cfg__chip--ok { color:var(--pos-success, #8BC34A); border-color:color-mix(in oklch, var(--pos-tint, #1F8B3A) 50%, transparent); background:var(--pos-tint-soft, color-mix(in oklch, #1F8B3A 14%, transparent)); }
  .dhk8-print-cfg__chip--warn { color:var(--pos-warning, #F5A623); border-color:color-mix(in oklch, #F5A623 40%, transparent); background:color-mix(in oklch, #F5A623 12%, transparent); }
  .dhk8-print-cfg__chip--err { color:var(--pos-danger, #E5484D); border-color:color-mix(in oklch, var(--pos-danger, #E5484D) 40%, transparent); background:color-mix(in oklch, var(--pos-danger, #E5484D) 12%, transparent); }
  .dhk8-print-cfg__chip--neutral { color:var(--pos-fg-secondary, #B6BFB8); }
  .dhk8-print-cfg__spin { animation:dhk8-print-cfg-spin 1.2s linear infinite; }
  @keyframes dhk8-print-cfg-spin { from { transform:rotate(0deg) } to { transform:rotate(360deg) } }

  .dhk8-print-cfg__kv { display:grid; grid-template-columns:120px 1fr; gap:6px 12px; margin:0; font-size:var(--pos-text-sm, 13px); }
  .dhk8-print-cfg__kv dt { color:var(--pos-fg-tertiary, #7E8C82); font-size:var(--pos-text-xs, 12px); text-transform:uppercase; letter-spacing:.06em; }
  .dhk8-print-cfg__kv dd { margin:0; color:var(--pos-fg, #E8EFE9); display:flex; flex-wrap:wrap; gap:6px; align-items:center; }
  .dhk8-print-cfg__tag { display:inline-block; padding:2px 8px; font-size:var(--pos-text-xxs, 11px); border-radius:var(--pos-radius-pill, 999px); background:var(--pos-elevated, #1E2520); border:1px solid var(--pos-separator, rgba(255,255,255,.07)); color:var(--pos-fg-secondary, #B6BFB8); font-family:var(--font-mono, ui-monospace, monospace); }
  .dhk8-print-cfg__tag--win { background:var(--pos-tint-soft, color-mix(in oklch, #1F8B3A 14%, transparent)); border-color:color-mix(in oklch, var(--pos-tint, #1F8B3A) 35%, transparent); color:var(--pos-success, #8BC34A); }
  .dhk8-print-cfg__tag--err { background:color-mix(in oklch, var(--pos-danger, #E5484D) 12%, transparent); border-color:color-mix(in oklch, var(--pos-danger, #E5484D) 40%, transparent); color:var(--pos-danger, #E5484D); }
  .dhk8-print-cfg__tag--sel { background:var(--pos-tint, #1F8B3A); color:var(--pos-on-tint, #fff); border-color:var(--pos-tint, #1F8B3A); }
  .dhk8-print-cfg__muted { color:var(--pos-fg-tertiary, #7E8C82); }
  .dhk8-print-cfg__small { font-size:var(--pos-text-xs, 12px); }

  .dhk8-print-cfg__note { margin:0; font-size:var(--pos-text-sm, 13px); color:var(--pos-fg-tertiary, #7E8C82); padding:var(--pos-space-3, 12px); border:1px dashed var(--pos-separator-strong, rgba(255,255,255,.14)); border-radius:var(--pos-radius-md, 8px); line-height:1.5; }
  .dhk8-print-cfg__note--err { color:var(--pos-danger, #E5484D); border-color:color-mix(in oklch, var(--pos-danger, #E5484D) 40%, transparent); }
  .dhk8-print-cfg__note--warn { color:var(--pos-warning, #F5A623); border-color:color-mix(in oklch, #F5A623 40%, transparent); }
  .dhk8-print-cfg__note--ok { color:var(--pos-success, #8BC34A); border-color:color-mix(in oklch, var(--pos-tint, #1F8B3A) 50%, transparent); }
  .dhk8-print-cfg__note code { font-family:var(--font-mono, ui-monospace, monospace); font-size:var(--pos-text-xs, 12px); padding:0 4px; background:var(--pos-canvas, #0F1411); border:1px solid var(--pos-separator, rgba(255,255,255,.07)); border-radius:var(--pos-radius-sm, 6px); }

  .dhk8-print-cfg__seg { display:grid; grid-template-columns:1fr 1fr; gap:var(--pos-space-2, 8px); }
  .dhk8-print-cfg__seg-opt { display:flex; align-items:center; gap:var(--pos-space-3, 12px); text-align:left; padding:var(--pos-space-3, 12px) var(--pos-space-4, 16px); min-height:var(--pos-touch-target, 44px); cursor:pointer; font:inherit; background:var(--pos-canvas, #0F1411); color:var(--pos-fg, #E8EFE9); border:1px solid var(--pos-separator, rgba(255,255,255,.07)); border-radius:var(--pos-radius-md, 8px); transition:border-color var(--pos-duration-fast, 120ms) ease, background var(--pos-duration-fast, 120ms) ease; }
  .dhk8-print-cfg__seg-opt:hover:not(.dhk8-print-cfg__seg-opt--active) { border-color:var(--pos-separator-strong, rgba(255,255,255,.14)); }
  .dhk8-print-cfg__seg-opt--active { border-color:var(--pos-tint, #1F8B3A); background:var(--pos-tint-soft, color-mix(in oklch, #1F8B3A 10%, transparent)); }
  .dhk8-print-cfg__seg-opt:focus-visible { outline:2px solid var(--pos-tint, #1F8B3A); outline-offset:2px; }
  .dhk8-print-cfg__seg-icon { display:inline-flex; flex-shrink:0; color:var(--pos-fg-secondary, #B6BFB8); }
  .dhk8-print-cfg__seg-opt--active .dhk8-print-cfg__seg-icon { color:var(--pos-success, #8BC34A); }
  .dhk8-print-cfg__seg-text { display:flex; flex-direction:column; gap:2px; min-width:0; }
  .dhk8-print-cfg__seg-title { font-size:var(--pos-text-sm, 13px); font-weight:var(--pos-weight-semibold, 600); }
  .dhk8-print-cfg__seg-sub { font-size:var(--pos-text-xs, 12px); color:var(--pos-fg-tertiary, #7E8C82); }

  .dhk8-print-cfg__cloudpub { display:flex; flex-direction:column; gap:var(--pos-space-3, 12px); margin-top:var(--pos-space-3, 12px); padding-top:var(--pos-space-3, 12px); border-top:1px solid var(--pos-separator, rgba(255,255,255,.07)); }
  .dhk8-print-cfg__cloudpub-head { display:flex; align-items:flex-start; gap:var(--pos-space-2, 8px); }
  .dhk8-print-cfg__cloudpub-icon { flex-shrink:0; margin-top:1px; color:var(--pos-fg-secondary, #B6BFB8); }
  .dhk8-print-cfg__cloudpub-text { display:flex; flex-direction:column; gap:2px; min-width:0; }
  .dhk8-print-cfg__cloudpub-title { font-size:var(--pos-text-sm, 13px); font-weight:var(--pos-weight-semibold, 600); color:var(--pos-fg, #E8EFE9); }
  .dhk8-print-cfg__cloudpub-sub { font-size:var(--pos-text-xs, 12px); color:var(--pos-fg-tertiary, #7E8C82); line-height:1.4; }
  .dhk8-print-cfg__cloudpub-howto { display:block; margin-top:8px; font-size:var(--pos-text-xs, 12px); color:var(--pos-fg-secondary, #B6BFB8); }
  .dhk8-print-cfg__paircode-row { display:flex; align-items:center; gap:var(--pos-space-2, 8px); flex-wrap:wrap; margin:8px 0; }
  .dhk8-print-cfg__paircode { font-family:var(--font-mono, ui-monospace, monospace); font-size:var(--pos-text-xl, 20px); font-weight:var(--pos-weight-semibold, 600); letter-spacing:.18em; padding:6px 14px; color:var(--pos-fg, #E8EFE9); background:var(--pos-canvas, #0F1411); border:1px solid var(--pos-tint, #1F8B3A); border-radius:var(--pos-radius-md, 8px); user-select:all; }

  .dhk8-print-cfg__fields { display:flex; flex-direction:column; gap:var(--pos-space-3, 12px); }
  .dhk8-print-cfg__field { display:flex; flex-direction:column; gap:6px; }
  .dhk8-print-cfg__field-label { font-size:var(--pos-text-sm, 13px); color:var(--pos-fg-secondary, #B6BFB8); font-weight:var(--pos-weight-medium, 500); }
  .dhk8-print-cfg__field-hint { font-size:var(--pos-text-xs, 12px); color:var(--pos-fg-tertiary, #7E8C82); line-height:1.4; }
  .dhk8-print-cfg__field-hint code { font-family:var(--font-mono, ui-monospace, monospace); padding:0 4px; background:var(--pos-canvas, #0F1411); border:1px solid var(--pos-separator, rgba(255,255,255,.07)); border-radius:var(--pos-radius-sm, 6px); }
  .dhk8-print-cfg__input { width:100%; box-sizing:border-box; min-height:var(--pos-touch-target, 44px); padding:var(--pos-space-3, 12px); font:inherit; font-size:var(--pos-text-sm, 13px); background:var(--pos-canvas, #0F1411); color:var(--pos-fg, #E8EFE9); border:1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); border-radius:var(--pos-radius-md, 8px); transition:border-color var(--pos-duration-fast, 120ms) ease; }
  .dhk8-print-cfg__input::placeholder { color:var(--pos-fg-tertiary, #7E8C82); }
  .dhk8-print-cfg__input:focus { outline:none; border-color:var(--pos-tint, #1F8B3A); }

  .dhk8-print-cfg__actions { display:flex; flex-wrap:wrap; gap:var(--pos-space-2, 8px); }
  .dhk8-print-cfg__btn { display:inline-flex; align-items:center; gap:var(--pos-space-2, 8px); padding:var(--pos-space-3, 12px) var(--pos-space-4, 16px); min-height:var(--pos-touch-target, 44px); cursor:pointer; font:inherit; font-size:var(--pos-text-sm, 13px); font-weight:var(--pos-weight-medium, 500); background:var(--pos-elevated, #1E2520); color:var(--pos-fg, #E8EFE9); border:1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); border-radius:var(--pos-radius-md, 8px); transition:background var(--pos-duration-fast, 120ms) ease, border-color var(--pos-duration-fast, 120ms) ease; }
  .dhk8-print-cfg__btn:hover:not(:disabled) { background:var(--pos-surface, #161C18); border-color:var(--pos-tint, #1F8B3A); }
  .dhk8-print-cfg__btn:disabled { opacity:.45; cursor:not-allowed; }
  .dhk8-print-cfg__ghost-btn { display:inline-flex; align-items:center; gap:6px; padding:6px 10px; min-height:32px; font:inherit; font-size:var(--pos-text-xs, 12px); background:transparent; color:var(--pos-fg-secondary, #B6BFB8); border:1px solid var(--pos-separator, rgba(255,255,255,.07)); border-radius:var(--pos-radius-pill, 999px); cursor:pointer; transition:border-color var(--pos-duration-fast, 120ms) ease, color var(--pos-duration-fast, 120ms) ease; }
  .dhk8-print-cfg__ghost-btn:hover { border-color:var(--pos-tint, #1F8B3A); color:var(--pos-fg, #E8EFE9); }

  .dhk8-print-cfg__printers { display:flex; flex-direction:column; gap:6px; }
  .dhk8-print-cfg__printer-list { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:4px; }
  .dhk8-print-cfg__printer-row { display:flex; justify-content:space-between; align-items:center; gap:var(--pos-space-3, 12px); width:100%; min-height:var(--pos-touch-target, 44px); padding:var(--pos-space-2, 8px) var(--pos-space-3, 12px); background:var(--pos-canvas, #0F1411); color:var(--pos-fg, #E8EFE9); border:1px solid var(--pos-separator, rgba(255,255,255,.07)); border-radius:var(--pos-radius-md, 8px); text-align:left; cursor:pointer; font:inherit; font-size:var(--pos-text-sm, 13px); transition:border-color var(--pos-duration-fast, 120ms) ease, background var(--pos-duration-fast, 120ms) ease; }
  .dhk8-print-cfg__printer-row:hover:not(:disabled) { border-color:var(--pos-tint, #1F8B3A); }
  .dhk8-print-cfg__printer-row--sel { border-color:var(--pos-tint, #1F8B3A); background:var(--pos-tint-soft, color-mix(in oklch, #1F8B3A 10%, transparent)); }
  .dhk8-print-cfg__printer-row:disabled { cursor:not-allowed; }
  .dhk8-print-cfg__printer-name { flex:1; min-width:0; word-break:break-word; }
  .dhk8-print-cfg__printer-tags { display:flex; flex-wrap:wrap; gap:4px; }

  .dhk8-print-cfg__cards { display:grid; grid-template-columns:repeat(auto-fill, minmax(240px, 1fr)); gap:var(--pos-space-3, 12px); }
  .dhk8-print-cfg__card { display:flex; flex-direction:column; gap:var(--pos-space-3, 12px); padding:var(--pos-space-4, 16px); background:var(--pos-surface, #161C18); border:1px solid var(--pos-separator, rgba(255,255,255,.07)); border-radius:var(--pos-radius-lg, 12px); }
  .dhk8-print-cfg__card-head { display:flex; justify-content:space-between; align-items:flex-start; gap:var(--pos-space-2, 8px); }
  .dhk8-print-cfg__card-title { margin:0; font-size:var(--pos-text-md, 15px); font-weight:var(--pos-weight-semibold, 600); color:var(--pos-fg, #E8EFE9); }
  .dhk8-print-cfg__card-sub { margin-top:2px; font-size:var(--pos-text-xs, 12px); color:var(--pos-fg-tertiary, #7E8C82); }
  .dhk8-print-cfg__copies { display:flex; align-items:center; gap:var(--pos-space-2, 8px); font-size:var(--pos-text-sm, 13px); color:var(--pos-fg-secondary, #B6BFB8); }
  .dhk8-print-cfg__copies--off { opacity:.45; }

  .dhk8-print-cfg__switch { position:relative; width:46px; min-width:46px; height:28px; padding:0; border:none; cursor:pointer; background:var(--pos-elevated, #1E2520); border-radius:999px; transition:background var(--pos-duration-fast, 120ms) ease; flex-shrink:0; }
  .dhk8-print-cfg__switch--on { background:var(--pos-tint, #1F8B3A); }
  .dhk8-print-cfg__switch-thumb { position:absolute; top:3px; left:3px; width:22px; height:22px; background:#fff; border-radius:50%; transition:transform var(--pos-duration-fast, 120ms) ease; box-shadow:0 1px 2px rgba(0,0,0,.3); }
  .dhk8-print-cfg__switch--on .dhk8-print-cfg__switch-thumb { transform:translateX(18px); }
  .dhk8-print-cfg__switch:focus-visible { outline:2px solid var(--pos-tint, #1F8B3A); outline-offset:2px; }

  .dhk8-print-cfg__spinner { display:inline-flex; align-items:center; gap:0; border:1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); border-radius:var(--pos-radius-md, 8px); overflow:hidden; }
  .dhk8-print-cfg__spinner-btn { min-width:32px; height:32px; padding:0; border:none; background:var(--pos-elevated, #1E2520); color:var(--pos-fg, #E8EFE9); font-size:16px; font-weight:600; cursor:pointer; transition:background var(--pos-duration-fast, 120ms) ease; }
  .dhk8-print-cfg__spinner-btn:disabled { opacity:.4; cursor:not-allowed; }
  .dhk8-print-cfg__spinner-btn:hover:not(:disabled) { background:var(--pos-surface, #161C18); }
  .dhk8-print-cfg__spinner-val { display:inline-block; min-width:32px; padding:4px 8px; text-align:center; background:var(--pos-canvas, #0F1411); color:var(--pos-fg, #E8EFE9); font-variant-numeric:tabular-nums; font-size:var(--pos-text-sm, 13px); border-left:1px solid var(--pos-separator, rgba(255,255,255,.07)); border-right:1px solid var(--pos-separator, rgba(255,255,255,.07)); }

  .dhk8-print-cfg__matrix { width:100%; border-collapse:collapse; font-size:var(--pos-text-sm, 13px); }
  .dhk8-print-cfg__matrix th { text-align:left; padding:var(--pos-space-2, 8px) var(--pos-space-3, 12px); border-bottom:1px solid var(--pos-separator, rgba(255,255,255,.07)); color:var(--pos-fg-tertiary, #7E8C82); text-transform:uppercase; letter-spacing:.06em; font-size:var(--pos-text-xxs, 11px); font-weight:var(--pos-weight-medium, 500); }
  .dhk8-print-cfg__matrix th:last-child, .dhk8-print-cfg__matrix td:last-child { text-align:right; width:80px; }
  .dhk8-print-cfg__matrix td { padding:var(--pos-space-3, 12px); border-bottom:1px solid var(--pos-separator, rgba(255,255,255,.07)); color:var(--pos-fg, #E8EFE9); }
  .dhk8-print-cfg__matrix tr:last-child td { border-bottom:none; }

  .dhk8-print-cfg__history { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:4px; }
  .dhk8-print-cfg__history-row { display:grid; grid-template-columns:auto 1fr auto auto; gap:var(--pos-space-3, 12px); align-items:center; padding:var(--pos-space-2, 8px) var(--pos-space-3, 12px); background:var(--pos-surface, #161C18); border:1px solid var(--pos-separator, rgba(255,255,255,.07)); border-radius:var(--pos-radius-md, 8px); font-size:var(--pos-text-xs, 12px); }
  .dhk8-print-cfg__history-when { color:var(--pos-fg-tertiary, #7E8C82); font-family:var(--font-mono, ui-monospace, monospace); font-variant-numeric:tabular-nums; }
  .dhk8-print-cfg__history-kind { display:inline-flex; align-items:center; gap:4px; color:var(--pos-fg-secondary, #B6BFB8); font-family:var(--font-mono, ui-monospace, monospace); }
  .dhk8-print-cfg__history-result { padding:2px 8px; font-size:var(--pos-text-xxs, 11px); font-weight:var(--pos-weight-semibold, 600); border-radius:var(--pos-radius-pill, 999px); letter-spacing:.04em; }
  .dhk8-print-cfg__history-result--ok { background:var(--pos-tint-soft, color-mix(in oklch, #1F8B3A 14%, transparent)); color:var(--pos-success, #8BC34A); }
  .dhk8-print-cfg__history-result--error { background:color-mix(in oklch, var(--pos-danger, #E5484D) 14%, transparent); color:var(--pos-danger, #E5484D); }
  .dhk8-print-cfg__history-err { grid-column:1/-1; color:var(--pos-danger, #E5484D); font-size:var(--pos-text-xxs, 11px); margin-top:2px; font-family:var(--font-mono, ui-monospace, monospace); }

  .dhk8-print-cfg__toast { position:fixed; bottom:var(--pos-space-6, 24px); left:50%; transform:translateX(-50%); padding:var(--pos-space-3, 12px) var(--pos-space-5, 20px); font-size:var(--pos-text-sm, 13px); border:1px solid var(--pos-separator, rgba(255,255,255,.07)); border-radius:var(--pos-radius-pill, 999px); background:var(--pos-elevated, #1E2520); box-shadow:var(--pos-shadow-3, 0 8px 20px -6px rgba(0,0,0,.35)); z-index:var(--pos-z-toast, 300); }
  .dhk8-print-cfg__toast--ok { border-color:color-mix(in oklch, var(--pos-tint, #1F8B3A) 50%, transparent); color:var(--pos-success, #8BC34A); }
  .dhk8-print-cfg__toast--err { border-color:color-mix(in oklch, var(--pos-danger, #E5484D) 40%, transparent); color:var(--pos-danger, #E5484D); }

  .dhk8-print-cfg__relay { display:flex; flex-direction:column; gap:var(--pos-space-3, 12px); }
  .dhk8-print-cfg__relay-head { display:flex; justify-content:space-between; align-items:flex-start; gap:var(--pos-space-3, 12px); flex-wrap:wrap; }
  .dhk8-print-cfg__relay-station { display:flex; align-items:flex-start; gap:var(--pos-space-2, 8px); min-width:0; }
  .dhk8-print-cfg__relay-station-icon { flex-shrink:0; margin-top:2px; color:var(--pos-fg-secondary, #B6BFB8); }
  .dhk8-print-cfg__relay-station-text { display:flex; flex-direction:column; gap:2px; min-width:0; }
  .dhk8-print-cfg__relay-station-name { font-size:var(--pos-text-md, 15px); font-weight:var(--pos-weight-semibold, 600); color:var(--pos-fg, #E8EFE9); word-break:break-word; }
  .dhk8-print-cfg__relay-state { display:inline-flex; align-items:center; gap:6px; font-size:var(--pos-text-xs, 12px); font-weight:var(--pos-weight-medium, 500); }
  .dhk8-print-cfg__relay-state--on { color:var(--pos-success, #8BC34A); }
  .dhk8-print-cfg__relay-state--off { color:var(--pos-fg-tertiary, #7E8C82); }
  .dhk8-print-cfg__relay-dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; background:currentColor; box-shadow:0 0 0 3px color-mix(in oklch, currentColor 22%, transparent); }
  .dhk8-print-cfg__relay-jobs-head { display:flex; justify-content:space-between; align-items:center; gap:var(--pos-space-2, 8px); flex-wrap:wrap; margin-top:var(--pos-space-1, 4px); }
  .dhk8-print-cfg__relay-jobs-actions { display:flex; gap:var(--pos-space-2, 8px); flex-wrap:wrap; }

  .dhk8-print-cfg__joblist { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:4px; }
  .dhk8-print-cfg__job { display:grid; grid-template-columns:auto auto 1fr auto; gap:var(--pos-space-2, 8px) var(--pos-space-3, 12px); align-items:center; padding:var(--pos-space-2, 8px) var(--pos-space-3, 12px); background:var(--pos-canvas, #0F1411); border:1px solid var(--pos-separator, rgba(255,255,255,.07)); border-radius:var(--pos-radius-md, 8px); font-size:var(--pos-text-xs, 12px); }
  .dhk8-print-cfg__job--ok { border-color:color-mix(in oklch, var(--pos-tint, #1F8B3A) 35%, transparent); }
  .dhk8-print-cfg__job--fail { border-color:color-mix(in oklch, var(--pos-danger, #E5484D) 40%, transparent); }
  .dhk8-print-cfg__job--retry { border-color:color-mix(in oklch, #F5A623 40%, transparent); }
  .dhk8-print-cfg__job-kind { display:inline-flex; align-items:center; gap:4px; color:var(--pos-fg-secondary, #B6BFB8); font-family:var(--font-mono, ui-monospace, monospace); }
  .dhk8-print-cfg__job-when { color:var(--pos-fg-tertiary, #7E8C82); font-family:var(--font-mono, ui-monospace, monospace); font-variant-numeric:tabular-nums; }
  .dhk8-print-cfg__job-status { justify-self:start; padding:2px 8px; border-radius:var(--pos-radius-pill, 999px); font-weight:var(--pos-weight-semibold, 600); font-size:var(--pos-text-xxs, 11px); white-space:nowrap; }
  .dhk8-print-cfg__job-status--pending { color:var(--pos-fg-secondary, #B6BFB8); background:var(--pos-elevated, #1E2520); }
  .dhk8-print-cfg__job-status--progress { color:var(--pos-info, #0091FF); background:color-mix(in oklch, var(--pos-info, #0091FF) 14%, transparent); }
  .dhk8-print-cfg__job-status--retry { color:var(--pos-warning, #F5A623); background:color-mix(in oklch, #F5A623 14%, transparent); }
  .dhk8-print-cfg__job-status--ok { color:var(--pos-success, #8BC34A); background:var(--pos-tint-soft, color-mix(in oklch, #1F8B3A 14%, transparent)); }
  .dhk8-print-cfg__job-status--fail { color:var(--pos-danger, #E5484D); background:color-mix(in oklch, var(--pos-danger, #E5484D) 14%, transparent); }
  .dhk8-print-cfg__job-retry { display:inline-flex; align-items:center; gap:4px; padding:4px 10px; min-height:30px; font:inherit; font-size:var(--pos-text-xxs, 11px); background:transparent; color:var(--pos-fg-secondary, #B6BFB8); border:1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); border-radius:var(--pos-radius-pill, 999px); cursor:pointer; transition:border-color var(--pos-duration-fast, 120ms) ease, color var(--pos-duration-fast, 120ms) ease; }
  .dhk8-print-cfg__job-retry:hover:not(:disabled) { border-color:var(--pos-tint, #1F8B3A); color:var(--pos-fg, #E8EFE9); }
  .dhk8-print-cfg__job-retry:disabled { opacity:.45; cursor:not-allowed; }
  .dhk8-print-cfg__job-err { grid-column:1/-1; color:var(--pos-danger, #E5484D); font-size:var(--pos-text-xxs, 11px); font-family:var(--font-mono, ui-monospace, monospace); word-break:break-word; }

  @media (max-width: 600px) {
    .dhk8-print-cfg__kv { grid-template-columns:1fr; gap:4px; }
    .dhk8-print-cfg__job { grid-template-columns:1fr auto; }
    .dhk8-print-cfg__job-status { justify-self:end; }
    .dhk8-print-cfg__kv dt { margin-top:8px; }
    .dhk8-print-cfg__cards { grid-template-columns:1fr; }
    .dhk8-print-cfg__seg { grid-template-columns:1fr; }
    .dhk8-print-cfg__history-row { grid-template-columns:1fr; gap:4px; }
    .dhk8-print-cfg__history-when, .dhk8-print-cfg__history-kind, .dhk8-print-cfg__history-result { width:auto; }
  }
`
